import React from "react";
import { MenuItem, TextField, Chip } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import clsx from "clsx";
import { orange } from "@mui/material/colors";

const useStyles = makeStyles((theme) => ({
    statusWorknest: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    statusCustom: {
        backgroundColor: orange[400],
        color: theme.palette.getContrastText(orange[400]),
    },
    chipWithPadding: {
        marginRight: theme.spacing(1),
    },
}));

function ResponsiveSelect({
    options = [],
    optionValueKey = null,
    optionLabelKey = null,
    optionLabelFunction = null,
    value,
    // Default to the compact size. In MUI v4 these selects were made dense via
    // `margin="dense"`, which no longer compacts height in v5+ — `size="small"`
    // does. Callers can still override by passing an explicit `size`.
    size = "small",
    ...rest
}) {
    const classes = useStyles();

    const displayOption = (selectOption, index) => {
        const value = optionValueKey ? selectOption[optionValueKey] : selectOption;
        const label = optionLabelKey
            ? selectOption[optionLabelKey]
            : optionLabelFunction
                ? optionLabelFunction(selectOption)
                : selectOption;

        const chipClass =
            selectOption.chipCaption === "Worknest"
                ? classes.statusWorknest
                : classes.statusCustom;

        return (
            <MenuItem key={index} value={value}>
                {selectOption.chipCaption && (
                    <Chip
                        className={clsx(classes.chipWithPadding, chipClass)}
                        size="small"
                        label={selectOption.chipCaption}
                    />
                )}
                {label}
            </MenuItem>
        );
    };

    // MUI warns ("out-of-range value") when a controlled Select is given a value
    // that isn't one of its options (or "" / []). The selected value can arrive
    // before its async options have loaded, so coerce null/undefined to "" AND
    // drop any value not currently present in `options`. Once the options include
    // it, it renders as selected again — no warning, same end state.
    // Ignore null/undefined entries — callers sometimes build options via
    // `[...].concat(data?.map(...))`, which yields `[..., undefined]` before the
    // async data loads. Filtering here keeps both the value-guard below and the
    // option render from crashing on an undefined option.
    const safeOptions = Array.isArray(options) ? options.filter((o) => o != null) : [];
    const getOptionValue = (option) =>
        optionValueKey ? option?.[optionValueKey] : option;
    const validValues = safeOptions.map(getOptionValue);
    const safeValue = Array.isArray(value)
        ? value.filter((v) => validValues.includes(v))
        : validValues.includes(value)
            ? value
            : "";

    return (
        <TextField
            select
            variant="outlined"
            size={size}
            value={safeValue}
            {...rest}
        >
            {safeOptions.map((option, index) => displayOption(option, index))}
        </TextField>
    );
}

export default ResponsiveSelect;